/**
 * Production Forecast Service
 * US-013D: API calls to production-forecast Edge Function
 */

import supabase from '@/supabase-client'
import { toLocalDateString } from '@/utils/dateUtils'

const FUNCTION_NAME = 'production-forecast'

/**
 * Call the production-forecast Edge Function
 */
async function callForecastFunction(body) {
  const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
    body
  })

  if (error) {
    console.error('[ProductionForecast] Function error:', error)
    throw new Error(error.message || 'Production forecast function failed')
  }

  return data
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Generate/regenerate the production plan
 *
 * @param {Object} options
 * @param {number} [options.weeks_ahead=26] - Number of weeks to plan ahead
 * @param {string} [options.strain_id] - Optional strain filter
 * @param {boolean} [options.force_regenerate=false] - Clear existing planned entries
 * @returns {Promise<Object>} Generation result
 */
export async function generateProductionPlan(options = {}) {
  return callForecastFunction({
    action: 'generate',
    weeks_ahead: options.weeks_ahead ?? 26,
    strain_id: options.strain_id,
    force_regenerate: options.force_regenerate ?? false
  })
}

/**
 * Get the current production plan
 *
 * @param {Object} options
 * @param {string} [options.strain_id] - Filter by strain
 * @param {string} [options.start_week] - Start date (YYYY-MM-DD)
 * @param {string} [options.end_week] - End date (YYYY-MM-DD)
 * @param {string[]} [options.status] - Filter by status
 * @returns {Promise<Object>} Plan data with entries
 */
export async function getProductionPlan(options = {}) {
  return callForecastFunction({
    action: 'get_plan',
    strain_id: options.strain_id,
    start_week: options.start_week,
    end_week: options.end_week,
    status: options.status
  })
}

/**
 * Update a production plan entry
 *
 * @param {string} planId - Plan entry ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated entry
 */
export async function updatePlanEntry(planId, updates) {
  return callForecastFunction({
    action: 'update_entry',
    plan_id: planId,
    updates
  })
}

/**
 * Get daily tasks from production plan
 *
 * @param {Object} options
 * @param {string} [options.date] - Target date (YYYY-MM-DD), default today
 * @param {number} [options.days_ahead=7] - Days to look ahead
 * @returns {Promise<Object>} Daily tasks
 */
export async function getDailyTasks(options = {}) {
  return callForecastFunction({
    action: 'daily_tasks',
    date: options.date,
    days_ahead: options.days_ahead ?? 7
  })
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Group plan entries by week
 *
 * @param {Array} entries - Plan entries
 * @returns {Map<string, Array>} Entries grouped by target_week
 */
export function groupPlanByWeek(entries) {
  const grouped = new Map()

  for (const entry of entries) {
    const week = entry.target_week
    if (!grouped.has(week)) {
      grouped.set(week, [])
    }
    grouped.get(week).push(entry)
  }

  return grouped
}

/**
 * Group plan entries by strain
 *
 * @param {Array} entries - Plan entries
 * @returns {Map<string, Array>} Entries grouped by strain name
 */
export function groupPlanByStrain(entries) {
  const grouped = new Map()

  for (const entry of entries) {
    const strainName = entry.strain?.name || 'Unknown'
    if (!grouped.has(strainName)) {
      grouped.set(strainName, [])
    }
    grouped.get(strainName).push(entry)
  }

  return grouped
}

/**
 * Calculate total demand by week
 *
 * @param {Array} entries - Plan entries
 * @returns {Map<string, number>} Total kg demand per week
 */
export function calculateWeeklyTotals(entries) {
  const totals = new Map()

  for (const entry of entries) {
    const week = entry.target_week
    const current = totals.get(week) || 0
    totals.set(week, current + (parseFloat(entry.demand_kg) || 0))
  }

  return totals
}

/**
 * Get entries with upcoming milestones
 *
 * @param {Array} entries - Plan entries
 * @param {number} daysAhead - Days to look ahead
 * @returns {Array} Entries with milestones in the window
 */
export function getUpcomingMilestones(entries, daysAhead = 14) {
  const today = toLocalDateString(new Date())
  const endDate = toLocalDateString(new Date(Date.now() + daysAhead * 24 * 60 * 60 * 1000))

  return entries.filter(entry => {
    const milestones = [
      entry.planned_lc_start,
      entry.planned_spawn_start,
      entry.planned_substrate_start,
      entry.planned_fruiting_start
    ].filter(Boolean)

    return milestones.some(date => date >= today && date <= endDate)
  })
}

/**
 * Identify gaps (weeks with no planned production for a strain)
 *
 * @param {Array} entries - Plan entries
 * @param {number} weeksAhead - Weeks to check
 * @returns {Array} Gaps with strain and week info
 */
export function identifyGaps(entries, weeksAhead = 26) {
  const gaps = []
  const strains = new Set(entries.map(e => e.strain?.name).filter(Boolean))

  // Build lookup of what's planned
  const planned = new Set()
  for (const entry of entries) {
    if (entry.strain?.name) {
      planned.add(`${entry.strain.name}:${entry.target_week}`)
    }
  }

  // Get all weeks in range
  const today = new Date()
  const weeks = []
  for (let i = 0; i < weeksAhead; i++) {
    const weekDate = new Date(today.getTime() + i * 7 * 24 * 60 * 60 * 1000)
    // Get Monday of that week
    const day = weekDate.getDay()
    const diff = weekDate.getDate() - day + (day === 0 ? -6 : 1)
    weekDate.setDate(diff)
    weeks.push(toLocalDateString(weekDate))
  }

  // Check each strain/week combination
  for (const strain of strains) {
    for (const week of weeks) {
      if (!planned.has(`${strain}:${week}`)) {
        gaps.push({
          strain,
          week,
          message: `No production planned for ${strain} in week of ${week}`
        })
      }
    }
  }

  return gaps
}

// =============================================================================
// Phase 3: Task Bumping & Recalculation Functions
// =============================================================================

/**
 * Get predefined bump reasons
 *
 * @returns {Promise<Object>} List of bump reasons
 */
export async function getBumpReasons() {
  return callForecastFunction({
    action: 'get_bump_reasons'
  })
}

/**
 * Preview the impact of bumping a task
 *
 * @param {string} taskId - Task ID (format: {task_type}-{source_id}-{planned_date})
 * @param {string} newDate - New date (YYYY-MM-DD)
 * @returns {Promise<Object>} Impact preview with dates, orders, conflicts
 */
export async function previewBumpImpact(taskId, newDate) {
  return callForecastFunction({
    action: 'preview_bump_impact',
    task_id: taskId,
    new_date: newDate
  })
}

/**
 * Bump (reschedule) a task to a new date
 *
 * @param {Object} options
 * @param {string} options.taskId - Task ID
 * @param {string} options.newDate - New date (YYYY-MM-DD)
 * @param {string} options.reason - Bump reason
 * @param {string} [options.customReason] - Custom reason if reason is 'other'
 * @param {string} [options.notes] - Additional notes
 * @returns {Promise<Object>} Bump result
 */
export async function bumpTask(options) {
  return callForecastFunction({
    action: 'bump_task',
    task_id: options.taskId,
    new_date: options.newDate,
    reason: options.reason,
    custom_reason: options.customReason,
    notes: options.notes
  })
}

/**
 * Complete a task
 *
 * @param {Object} options
 * @param {string} options.taskId - Task ID
 * @param {string} [options.actualDate] - Actual completion date (defaults to today)
 * @param {string} [options.notes] - Completion notes
 * @param {number} [options.quantity] - Quantity (e.g., kg harvested)
 * @returns {Promise<Object>} Completion result
 */
export async function completeTask(options) {
  return callForecastFunction({
    action: 'complete_task',
    task_id: options.taskId,
    actual_date: options.actualDate,
    notes: options.notes,
    quantity: options.quantity
  })
}

/**
 * Recalculate batch timeline after variance
 *
 * @param {string} batchId - Production plan ID
 * @param {number} [varianceDays] - Days of variance (calculated if not provided)
 * @returns {Promise<Object>} Recalculation result
 */
export async function recalculateBatch(batchId, varianceDays) {
  return callForecastFunction({
    action: 'recalculate_batch',
    batch_id: batchId,
    variance_days: varianceDays
  })
}

/**
 * Get order impact for a batch
 *
 * @param {string} batchId - Production plan ID
 * @param {string} [newHarvestDate] - Proposed new harvest date
 * @returns {Promise<Object>} Order impact assessment
 */
export async function getOrderImpact(batchId, newHarvestDate) {
  return callForecastFunction({
    action: 'get_order_impact',
    batch_id: batchId,
    new_harvest_date: newHarvestDate
  })
}

export default {
  generateProductionPlan,
  getProductionPlan,
  updatePlanEntry,
  getDailyTasks,
  groupPlanByWeek,
  groupPlanByStrain,
  calculateWeeklyTotals,
  getUpcomingMilestones,
  identifyGaps,
  // Phase 3
  getBumpReasons,
  previewBumpImpact,
  bumpTask,
  completeTask,
  recalculateBatch,
  getOrderImpact
}
